/**
 * useCancelAlternativesState — où mènent les gestes de l'alternative (étape 2).
 *
 * Port littéral de `CancelAlternativesScreen.js:146-197` : trois natures d'action, toutes
 * existantes — Calendly avec le motif pré-rempli, retour au portail avec `openSupport`, retour au
 * portail avec `triggerPause` — plus le repli Calendly « conseiller » quand une action n'a pas de
 * nature, pour qu'aucun bouton ne soit mort.
 *
 * `supportIntent` / `supportMotif` ne sont pas repris : personne ne les lit
 * (`ParentSubscriptionPortalScreen.js:298-312`).
 */
import { useCallback, useMemo } from "react"
import { useNavigation } from "@react-navigation/native"

import { openCalendly } from "../../../../../../data/utility/Utils"
import {
    cancelCalendlyFallbackLabel,
    cancelCalendlyLabelForVariant,
    CANCEL_ALTERNATIVE_ACTIONS,
} from "../../../../adapters/parent/SubscriptionCancel/subscriptionCancelAdapter"
import {
    backToSubscriptionPortal,
    nextScreenAfterAlternatives,
    type CancelFlowNavigation,
    type CancelFlowRouteParams,
} from "./cancelFlowNavigation"

export type UseCancelAlternativesStateArgs = {
    routeParams: CancelFlowRouteParams
    /** Le blob `currentUser` que Calendly pré-remplit — vient du view-model. */
    calendlySubject: unknown
}

export function useCancelAlternativesState(args: UseCancelAlternativesStateArgs) {
    const { routeParams, calendlySubject } = args
    const navigation = useNavigation<CancelFlowNavigation>()
    const motif = routeParams.motif ?? null

    const onAction = useCallback(
        (key: string) => {
            const spec = CANCEL_ALTERNATIVE_ACTIONS[key]

            if (spec?.kind === "calendly") {
                openCalendly(calendlySubject, "cancel", cancelCalendlyLabelForVariant(motif, spec.variant))
                return
            }
            if (spec?.kind === "support_modal") {
                backToSubscriptionPortal(navigation, { openSupport: true })
                return
            }
            if (spec?.kind === "pause_flow") {
                backToSubscriptionPortal(navigation, { triggerPause: true, fromCancellation: true })
                return
            }

            openCalendly(calendlySubject, "cancel", cancelCalendlyFallbackLabel(motif))
        },
        [calendlySubject, motif, navigation]
    )

    return useMemo(
        () => ({
            onAction,
            onContinue: () => navigation.push(nextScreenAfterAlternatives(), { ...routeParams }),
            onBack: () => navigation.goBack(),
            onClose: () => navigation.popToTop(),
        }),
        [navigation, onAction, routeParams]
    )
}
